import { TreatmentRecord, TreatmentRecordInput } from '../types/treatment';
import logger from '../utils/logger';
import { DataService } from './dataService';

/**
 * 施術記録の管理を行うサービスクラス
 * DataService経由でSupabase/localStorage統合データ管理
 * Issue #236: LocalStorage → Supabase 段階的移行対応
 */
export class TreatmentService {

  /**
   * 指定顧客の全施術記録を取得
   * @param customerId 顧客ID
   * @returns 施術記録の配列（新しい順）
   */
  static async getTreatmentRecords(customerId: number): Promise<TreatmentRecord[]> {
    try {
      return await DataService.getTreatmentRecords(customerId);
    } catch (error) {
      logger.error('Failed to load treatment records:', error);
      return [];
    }
  }

  /**
   * 指定顧客の最新施術記録を取得
   * @param customerId 顧客ID
   * @returns 最新の施術記録またはnull
   */
  static async getLatestTreatmentRecord(customerId: number): Promise<TreatmentRecord | null> {
    try {
      return await DataService.getLatestTreatmentRecord(customerId);
    } catch (error) {
      logger.error('Failed to get latest treatment record:', error);
      return null;
    }
  }

  /**
   * 新規施術記録を保存
   * @param customerId 顧客ID
   * @param record 施術記録入力データ
   * @returns 保存された施術記録
   */
  static async saveTreatmentRecord(customerId: number, record: TreatmentRecordInput): Promise<TreatmentRecord> {
    try {
      return await DataService.saveTreatmentRecord(customerId, record);
    } catch (error) {
      logger.error('Failed to save treatment record:', error);
      throw new Error('施術記録の保存に失敗しました');
    }
  }

  /**
   * 既存施術記録を更新
   * @param customerId 顧客ID
   * @param id 施術記録ID
   * @param updates 更新データ
   * @returns 更新された施術記録またはnull
   */
  static async updateTreatmentRecord(
    customerId: number, 
    id: string, 
    updates: Partial<TreatmentRecord>
  ): Promise<TreatmentRecord | null> {
    try {
      return await DataService.updateTreatmentRecord(customerId, id, updates);
    } catch (error) {
      logger.error('Failed to update treatment record:', error);
      throw new Error('施術記録の更新に失敗しました');
    }
  }

  /**
   * 指定期間の施術記録を取得
   * @param customerId 顧客ID
   * @param startDate 開始日
   * @param endDate 終了日
   * @returns フィルターされた施術記録
   */
  static async getTreatmentRecordsByDateRange(
    customerId: number,
    startDate: Date,
    endDate: Date
  ): Promise<TreatmentRecord[]> {
    try {
      return await DataService.getTreatmentRecordsByDateRange(customerId, startDate, endDate);
    } catch (error) {
      logger.error('Failed to get treatment records by date range:', error);
      return [];
    }
  }

  /**
   * 指定顧客の来店回数を取得（現在の訪問を含む）
   * @param customerId 顧客ID
   * @returns 来店回数
   */
  static async getVisitCount(customerId: number): Promise<number> {
    try {
      return await DataService.getVisitCount(customerId);
    } catch (error) {
      logger.error('Failed to get visit count:', error);
      return 1; // エラー時のデフォルト値
    }
  }

  /**
   * 施術記録を削除
   * @param customerId 顧客ID
   * @param id 施術記録ID
   * @returns 削除成功フラグ
   */
  static async deleteTreatmentRecord(customerId: number, id: string): Promise<boolean> {
    try {
      return await DataService.deleteTreatmentRecord(customerId, id);
    } catch (error) {
      logger.error('Failed to delete treatment record:', error);
      return false;
    }
  }
}